import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { 
  Home, ArrowUpCircle, ArrowDownCircle, History, User, Settings, 
  Shield, LogOut, Bell, TrendingUp, Wallet, CheckCircle, Star,
  MessageSquare, Filter, Download, Search, CreditCard, Calendar
} from "lucide-react";

export default function BybitWallet() {
  const [searchParams] = useSearchParams();
  
  // Synchronous initialization from localStorage for Puppeteer screenshots
  const getInitialData = () => {
    try {
      const stored = localStorage.getItem('bybit_preview_data');
      if (stored) return JSON.parse(stored);
    } catch (e) {}
    return null;
  };
  
  const getInitialDateFrom = () => {
    const data = getInitialData();
    return data?.dateFrom || '';
  };
  
  const getInitialDateTo = () => {
    const data = getInitialData();
    return data?.dateTo || '';
  };
  
  const [data, setData] = useState(() => getInitialData());
  const [filter, setFilter] = useState('all');
  const [showBrowser, setShowBrowser] = useState(() => {
    const data = getInitialData();
    return data?.showBrowser !== undefined ? data.showBrowser : true;
  });
  const [dateFrom, setDateFrom] = useState(() => getInitialDateFrom());
  const [dateTo, setDateTo] = useState(() => getInitialDateTo());

  // Set body background for Bybit theme
  useEffect(() => {
    const rootEl = document.getElementById('root');
    document.documentElement.classList.add('bybit-theme');
    document.body.classList.add('bybit-theme');
    document.body.style.cssText = 'margin: 0; padding: 0; background-color: #1a1a2e; min-height: 100vh;';
    document.documentElement.style.cssText = 'background-color: #1a1a2e; min-height: 100vh;';
    if (rootEl) {
      rootEl.classList.add('bybit-theme');
      rootEl.style.cssText = 'min-height: 100vh; background-color: #1a1a2e;';
    }
    return () => {
      document.documentElement.classList.remove('bybit-theme');
      document.body.classList.remove('bybit-theme');
      document.body.style.cssText = '';
      document.documentElement.style.cssText = '';
      if (rootEl) {
        rootEl.classList.remove('bybit-theme');
        rootEl.style.cssText = '';
      }
    };
  }, []);

  useEffect(() => {
    const browserParam = searchParams.get('showBrowser');
    const storageKey = searchParams.get('storageKey');
    const dataParam = searchParams.get('data');
    let parsed = null;
    
    if (browserParam !== null) setShowBrowser(browserParam !== 'false');
    
    // Priority 1: Try data from URL (for archive screenshots)
    if (dataParam) {
      try {
        parsed = JSON.parse(decodeURIComponent(dataParam));
      } catch (e) {
        console.error("Error parsing URL data:", e);
      }
    }
    
    // Priority 2: Try storageKey
    if (!parsed && storageKey) {
      try {
        const stored = localStorage.getItem(storageKey);
        if (stored) parsed = JSON.parse(stored);
      } catch (e) {}
    }
    
    // Priority 3: Try standard localStorage key
    if (!parsed) {
      try {
        const stored = localStorage.getItem('bybit_preview_data');
        if (stored) parsed = JSON.parse(stored);
      } catch (e) {}
    }
    
    if (parsed) {
      setData(parsed);
      if (parsed.showBrowser !== undefined) setShowBrowser(parsed.showBrowser);
      // Set date filter from data if provided
      if (parsed.dateFrom) setDateFrom(parsed.dateFrom);
      if (parsed.dateTo) setDateTo(parsed.dateTo);
    }
  }, [searchParams]);

  // Browser Frame Component
  const BrowserFrame = () => {
    const theme = {
      tabBarBg: '#202124',
      activeTabBg: '#35363a',
      inactiveTabBg: '#202124',
      addressBarBg: '#292a2d',
      textPrimary: '#e8eaed',
      textSecondary: '#9aa0a6',
    };

    return (
      <div style={{ backgroundColor: theme.tabBarBg }} className="flex-shrink-0">
        <div className="flex items-center h-[38px]" style={{ backgroundColor: theme.tabBarBg }}>
          <div className="flex items-end flex-1 pl-2">
            <div className="flex items-center h-[34px] px-3 rounded-t-lg min-w-[140px] max-w-[200px] mr-[1px]" style={{ backgroundColor: theme.inactiveTabBg }}>
              <img src="/ozon-favicon.ico" alt="" className="w-4 h-4 mr-2 flex-shrink-0" />
              <span className="text-[13px] truncate flex-1" style={{ color: theme.textSecondary }}>Ozon Карта</span>
            </div>
            <div className="flex items-center h-[34px] px-3 rounded-t-lg min-w-[180px] max-w-[240px]" style={{ backgroundColor: theme.activeTabBg }}>
              <span className="text-[13px] truncate flex-1" style={{ color: theme.textPrimary }}>Трейдинг P2P</span>
              <span className="ml-2 text-[13px]" style={{ color: theme.textSecondary }}>×</span>
            </div>
          </div>
        </div>
        <div className="flex items-center h-[40px] px-4 gap-4" style={{ backgroundColor: theme.addressBarBg }}>
          <div className="flex gap-2">
            {['←', '→', '↻'].map((arrow, i) => (
              <button key={i} className="w-8 h-8 rounded-full flex items-center justify-center" style={{ color: theme.textSecondary }}>
                {arrow}
              </button>
            ))}
          </div>
          <div className="flex-1 h-[32px] rounded-full px-4 flex items-center" style={{ backgroundColor: '#202124' }}>
            <span className="text-[14px]" style={{ color: theme.textSecondary }}>🔒 treider2p.com</span>
          </div>
        </div>
      </div>
    );
  };

  if (!data) {
    return (
      <div className="min-h-screen bg-[#0d0d1a] flex items-center justify-center">
        <div className="text-white text-lg">Загрузка...</div>
      </div>
    );
  }

  const menuItems = [
    { icon: Home, label: 'Главная' },
    { icon: TrendingUp, label: 'P2P Торговля' },
    { icon: Wallet, label: 'Кошелёк', active: true },
    { icon: History, label: 'История' },
    { icon: MessageSquare, label: 'Сообщения' },
    { icon: User, label: 'Профиль' },
    { icon: Shield, label: 'Безопасность' },
    { icon: Settings, label: 'Настройки' },
  ];

  const allTransactions = data.transactions || [];
  
  // Parse date from DD.MM.YYYY or DD.MM.YYYY, HH:MM to Date object
  const parseDate = (dateStr) => {
    if (!dateStr) return new Date();
    // Handle format "DD.MM.YYYY, HH:MM" or "DD.MM.YYYY"
    const dateOnly = dateStr.split(',')[0].trim();
    const parts = dateOnly.split('.');
    if (parts.length === 3) {
      const [day, month, year] = parts;
      // Create date at noon to avoid timezone issues
      return new Date(parseInt(year), parseInt(month) - 1, parseInt(day), 12, 0, 0);
    }
    return new Date();
  };
  
  // Format date for input (DD.MM.YYYY) without timezone conversion
  const formatDateForInput = (date) => {
    try {
      const d = new Date(date);
      if (isNaN(d.getTime())) return '';
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${day}.${month}.${year}`;
    } catch (e) {
      return '';
    }
  };
  
  // Parse DD.MM.YYYY to Date object
  const parseInputDate = (dateStr) => {
    if (!dateStr) return null;
    const parts = dateStr.split('.');
    if (parts.length === 3) {
      const [day, month, year] = parts;
      return new Date(parseInt(year), parseInt(month) - 1, parseInt(day), 12, 0, 0);
    }
    return null;
  };
  
  // Get min and max dates from ALL transactions (including random operations)
  const transactionDates = allTransactions.map(tx => parseDate(tx.date)).filter(d => !isNaN(d.getTime()));
  const minDate = transactionDates.length > 0 ? new Date(Math.min(...transactionDates)) : new Date();
  const maxDate = transactionDates.length > 0 ? new Date(Math.max(...transactionDates)) : new Date();
  
  // Get dateFrom/dateTo directly from data (important for archive screenshots)
  // This ensures the filter shows correct dates even on first render
  const dataDateFrom = data.dateFrom || '';
  const dataDateTo = data.dateTo || '';
  
  // Priority: 1) state dateFrom/dateTo (user changed), 2) data.dateFrom/dateTo (from generation), 3) calculated from transactions
  const effectiveDateFrom = dateFrom || dataDateFrom || formatDateForInput(minDate);
  const effectiveDateTo = dateTo || dataDateTo || formatDateForInput(maxDate);
  
  // Apply date filter - parse DD.MM.YYYY format
  const fromDateParsed = parseInputDate(effectiveDateFrom);
  const toDateParsed = parseInputDate(effectiveDateTo);
  const fromDate = fromDateParsed ? new Date(fromDateParsed.getFullYear(), fromDateParsed.getMonth(), fromDateParsed.getDate(), 0, 0, 0) : minDate;
  const toDate = toDateParsed ? new Date(toDateParsed.getFullYear(), toDateParsed.getMonth(), toDateParsed.getDate(), 23, 59, 59) : maxDate;
  
  const dateFilteredTransactions = allTransactions.filter(tx => {
    const txDate = parseDate(tx.date);
    return txDate >= fromDate && txDate <= toDate;
  });
  
  // Then apply type filter
  const filteredTransactions = dateFilteredTransactions.filter(tx => {
    if (filter === 'deposit') return tx.type === 'deposit';
    if (filter === 'withdrawal') return tx.type === 'withdrawal';
    return true;
  });

  // Calculate totals for the entire date period (not affected by type filter)
  const totalDeposits = dateFilteredTransactions.filter(t => t.type === 'deposit').reduce((sum, t) => sum + (t.amount || 0), 0);
  const totalWithdrawals = dateFilteredTransactions.filter(t => t.type === 'withdrawal').reduce((sum, t) => sum + (t.amount || 0), 0);
  const depositsCount = dateFilteredTransactions.filter(t => t.type === 'deposit').length;
  const withdrawalsCount = dateFilteredTransactions.filter(t => t.type === 'withdrawal').length;

  return (
    <div className="h-screen bg-[#0d0d1a] flex flex-col" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      {showBrowser && <BrowserFrame />}
      <div className="flex flex-1 min-h-0">
      {/* Sidebar */}
      <aside className="w-64 bg-[#1a1a2e] flex flex-col border-r border-gray-800 flex-shrink-0">
        <div className="p-5 border-b border-gray-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-white font-bold text-xl">P2P Exchange</h1>
              <p className="text-gray-500 text-xs">Crypto Trading</p>
            </div>
          </div>
        </div>

        <nav className="flex-1 py-4">
          {menuItems.map((item, index) => {
            const Icon = item.icon;
            return (
              <button
                key={index}
                className={`w-full flex items-center gap-3 px-5 py-3 text-sm transition-all ${
                  item.active 
                    ? 'bg-amber-500/10 text-amber-400 border-r-2 border-amber-400' 
                    : 'text-gray-400 hover:bg-white/5 hover:text-white'
                }`}
              >
                <Icon className="w-5 h-5" />
                <span>{item.label}</span>
              </button>
            );
          })}
        </nav>

        <div className="p-4 border-t border-gray-800">
          <button className="w-full flex items-center gap-3 px-4 py-2.5 text-gray-400 hover:text-white hover:bg-white/5 rounded-lg">
            <LogOut className="w-5 h-5" />
            <span>Выйти</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-0 bg-[#0d0d1a]">
        <header className="flex-shrink-0 bg-[#1a1a2e] border-b border-gray-800 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-white">Кошелёк</h2>
            <p className="text-gray-500 text-sm">История пополнений и выводов</p>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 px-3 py-1.5 bg-green-500/10 border border-green-500/30 rounded-full">
              <CheckCircle className="w-4 h-4 text-green-400" />
              <span className="text-green-400 text-sm font-medium">Верифицирован</span>
            </div>
            
            <div className="flex items-center gap-3 pl-4 border-l border-gray-700">
              <div className="w-9 h-9 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center">
                <span className="text-white text-sm font-bold">
                  {data.firstName?.[0]}{data.lastName?.[0]}
                </span>
              </div>
              <div>
                <p className="text-sm font-medium text-white">{data.fullName}</p>
                <p className="text-xs text-gray-500">ID: {data.userId}</p>
              </div>
            </div>
          </div>
        </header>

        <main className="flex-1 p-6 overflow-auto min-h-0 bg-[#0d0d1a]">
          {/* Stats Cards */}
          <div className="grid grid-cols-4 gap-4 mb-6">
            <div className="bg-[#1a1a2e] rounded-xl p-4 border border-gray-800">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-amber-500/10 rounded-lg flex items-center justify-center">
                  <Wallet className="w-5 h-5 text-amber-400" />
                </div>
                <p className="text-gray-400 text-sm">Баланс</p>
              </div>
              <p className="text-white text-2xl font-bold">{data.balance?.toLocaleString()} ₽</p>
            </div>
            <div className="bg-[#1a1a2e] rounded-xl p-4 border border-gray-800">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-green-500/10 rounded-lg flex items-center justify-center">
                  <ArrowUpCircle className="w-5 h-5 text-green-400" />
                </div>
                <p className="text-gray-400 text-sm">Пополнено</p>
              </div>
              <p className="text-green-400 text-2xl font-bold">+{totalDeposits.toLocaleString()} ₽</p>
              <p className="text-gray-500 text-xs mt-1">{depositsCount} операций</p>
            </div>
            <div className="bg-[#1a1a2e] rounded-xl p-4 border border-gray-800">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-red-500/10 rounded-lg flex items-center justify-center">
                  <ArrowDownCircle className="w-5 h-5 text-red-400" />
                </div>
                <p className="text-gray-400 text-sm">Выведено</p>
              </div>
              <p className="text-red-400 text-2xl font-bold">-{totalWithdrawals.toLocaleString()} ₽</p>
              <p className="text-gray-500 text-xs mt-1">{withdrawalsCount} операций</p>
            </div>
            <div className="bg-[#1a1a2e] rounded-xl p-4 border border-gray-800">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-purple-500/10 rounded-lg flex items-center justify-center">
                  <CreditCard className="w-5 h-5 text-purple-400" />
                </div>
                <p className="text-gray-400 text-sm">Всего операций</p>
              </div>
              <p className="text-white text-2xl font-bold">{allTransactions.length}</p>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <button className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 rounded-xl p-4 flex items-center gap-4 transition-all">
              <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                <ArrowUpCircle className="w-6 h-6 text-white" />
              </div>
              <div className="text-left">
                <p className="text-white font-semibold">Пополнить</p>
                <p className="text-green-200 text-sm">Карта, СБП, криптовалюта</p>
              </div>
            </button>
            <button className="bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700 rounded-xl p-4 flex items-center gap-4 transition-all">
              <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                <ArrowDownCircle className="w-6 h-6 text-white" />
              </div>
              <div className="text-left">
                <p className="text-white font-semibold">Вывести</p>
                <p className="text-red-200 text-sm">На карту, кошелёк, биржу</p>
              </div>
            </button>
          </div>

          {/* Transactions Table */}
          <div className="bg-[#1a1a2e] rounded-2xl border border-gray-800 overflow-hidden">
            <div className="p-4 border-b border-gray-800 flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <h3 className="font-semibold text-white flex items-center gap-2">
                    <History className="w-5 h-5 text-amber-400" />
                    История операций
                  </h3>
                  <div className="flex gap-1 bg-[#0d0d1a] p-1 rounded-lg">
                    {[
                      { id: 'all', label: 'Все' },
                      { id: 'deposit', label: 'Пополнения' },
                      { id: 'withdrawal', label: 'Выводы' },
                    ].map(f => (
                      <button
                        key={f.id}
                        onClick={() => setFilter(f.id)}
                        className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                          filter === f.id 
                            ? 'bg-amber-500 text-white' 
                            : 'text-gray-400 hover:text-white'
                        }`}
                      >
                        {f.label}
                      </button>
                    ))}
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <button className="flex items-center gap-2 px-4 py-2 text-gray-400 hover:text-white border border-gray-700 rounded-lg text-sm">
                    <Download className="w-4 h-4" />
                    Экспорт
                  </button>
                </div>
              </div>
              
              {/* Date Filter */}
              <div className="flex items-center gap-3 bg-[#0d0d1a] p-3 rounded-lg">
                <Calendar className="w-4 h-4 text-amber-400" />
                <span className="text-sm text-gray-400">Период:</span>
                <span className="text-sm text-gray-500">с</span>
                <input
                  type="text"
                  placeholder="ДД.ММ.ГГГГ"
                  value={effectiveDateFrom}
                  onChange={(e) => {
                    let val = e.target.value.replace(/[^\d.]/g, '');
                    if (val.length === 2 && !val.includes('.')) val += '.';
                    if (val.length === 5 && val.split('.').length === 2) val += '.';
                    if (val.length <= 10) setDateFrom(val);
                  }}
                  className="px-3 py-1.5 rounded-lg text-sm bg-[#1a1a2e] border border-gray-700 text-white focus:outline-none focus:border-amber-500 w-28"
                />
                <span className="text-sm text-gray-500">по</span>
                <input
                  type="text"
                  placeholder="ДД.ММ.ГГГГ"
                  value={effectiveDateTo}
                  onChange={(e) => {
                    let val = e.target.value.replace(/[^\d.]/g, '');
                    if (val.length === 2 && !val.includes('.')) val += '.';
                    if (val.length === 5 && val.split('.').length === 2) val += '.';
                    if (val.length <= 10) setDateTo(val);
                  }}
                  className="px-3 py-1.5 rounded-lg text-sm bg-[#1a1a2e] border border-gray-700 text-white focus:outline-none focus:border-amber-500 w-28"
                />
                {(dateFrom || dateTo) && (
                  <button
                    onClick={() => { setDateFrom(''); setDateTo(''); }}
                    className="text-xs text-amber-400 hover:text-amber-300 px-2"
                  >
                    Сбросить
                  </button>
                )}
                <span className="text-xs text-gray-500 ml-auto">{filteredTransactions.length} операций</span>
              </div>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#0d0d1a]">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Тип</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Дата</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Сумма</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Способ</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Статус</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-800">
                  {filteredTransactions.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                        Нет операций за выбранный период
                      </td>
                    </tr>
                  ) : (
                    filteredTransactions.map((tx, index) => (
                      <tr key={index} className="hover:bg-white/5 transition-colors">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                              tx.type === 'deposit' ? 'bg-green-500/10' : 'bg-red-500/10'
                            }`}>
                              {tx.icon ? (
                                <span className="text-lg">{tx.icon}</span>
                              ) : tx.type === 'deposit' ? (
                                <ArrowUpCircle className="w-5 h-5 text-green-400" />
                              ) : (
                                <ArrowDownCircle className="w-5 h-5 text-red-400" />
                              )}
                            </div>
                            <div>
                              <p className="text-white font-medium">{tx.description || (tx.type === 'deposit' ? 'Пополнение' : 'Вывод')}</p>
                              {tx.sender && !tx.icon && (
                                <p className="text-gray-500 text-xs">{tx.sender}</p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-gray-400">{tx.date} {tx.time}</span>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`font-semibold text-lg ${tx.type === 'deposit' ? 'text-green-400' : 'text-red-400'}`}>
                            {tx.type === 'deposit' ? '+' : '-'}{tx.amount?.toLocaleString()} ₽
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-3 py-1.5 text-sm rounded-lg ${
                            tx.method === 'Перевод по СБП' 
                              ? 'bg-blue-500/10 text-blue-400' 
                              : tx.method === 'Перевод по карте'
                                ? 'bg-purple-500/10 text-purple-400'
                                : 'bg-amber-500/10 text-amber-400'
                          }`}>
                            {tx.method || 'Карта'}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="px-3 py-1.5 text-sm rounded-lg bg-green-500/10 text-green-400">
                            Выполнено
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Summary Footer */}
            <div className="p-4 border-t border-gray-800 bg-[#0d0d1a] flex items-center justify-between">
              <div className="text-gray-500 text-sm">
                Показано {filteredTransactions.length} из {dateFilteredTransactions.length} операций
              </div>
              <div className="flex items-center gap-6 text-sm">
                <div className="flex items-center gap-2">
                  <ArrowUpCircle className="w-4 h-4 text-green-400" />
                  <span className="text-gray-500">Пополнено:</span>
                  <span className="text-green-400 font-semibold">+{totalDeposits.toLocaleString()} ₽</span>
                </div>
                <div className="flex items-center gap-2">
                  <ArrowDownCircle className="w-4 h-4 text-red-400" />
                  <span className="text-gray-500">Выведено:</span>
                  <span className="text-red-400 font-semibold">-{totalWithdrawals.toLocaleString()} ₽</span>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
      </div>
    </div>
  );
}
